import Big from 'big.js';
import { useGetStateItem } from './casper-service-hooks';
import {
  FTAmountDataType,
  DeployContractPackageResult,
  URefRawData,
} from '@block-explorer/entities';
import { useGetURefDetailsFromRawData } from '../scenes/URefScene/hooks/use-get-uref-details-from-raw-data';

type Cep18DataType = {
  data: Omit<FTAmountDataType, 'totalSupply'>;
};

type Cep18DataWithTotalSupplyType = {
  data: FTAmountDataType;
  loading: boolean;
};

type GetCep18AmountFromMetadataProps = {
  contractPackageInfo: DeployContractPackageResult | undefined | null;
  balance?: string;
  isShiboo?: boolean;
};

export const tokenDivider = (decimals) => Big(10).pow(decimals || 0);

export const useGetCep18AmountFromMetadata = (
  props: GetCep18AmountFromMetadataProps | null
): Cep18DataType => {
  if (props?.isShiboo) {
    return {
      data: {
        symbol: 'SHIBOO',
        decimals: 18,
        cep18Balance: '0',
      },
    };
  }

  if (!props?.contractPackageInfo) {
    return {
      data: {
        symbol: null,
        decimals: null,
        cep18Balance: null,
      },
    };
  }

  const { metadata } = props.contractPackageInfo;
  const calculatedCep18Balance = props?.balance
    ? Big(props?.balance || 0)
        .div(tokenDivider(metadata?.decimals))
        .toString()
    : '0';

  return {
    data: {
      symbol: metadata?.symbol ? metadata?.symbol : null,
      decimals: metadata?.decimals ? metadata?.decimals : null,
      cep18Balance: calculatedCep18Balance,
    },
  };
};

export const useGetCep18AmountWithTotalSupplyFromMetadata = (
  props: GetCep18AmountFromMetadataProps | null
): Cep18DataWithTotalSupplyType => {
  const cep18TotalSupplyURefKey =
    props?.contractPackageInfo?.metadata?.total_supply_uref;
  const {
    data: cep18TotalSupplyURefRawData,
    loading: cep18TotalSupplyURefRawLoading,
  } = useGetStateItem<URefRawData>(
    cep18TotalSupplyURefKey ? { key: cep18TotalSupplyURefKey } : null
  );

  const cep18TotalSupplyURefData = useGetURefDetailsFromRawData(
    cep18TotalSupplyURefKey && cep18TotalSupplyURefRawData
      ? {
          CLValueData: cep18TotalSupplyURefRawData.CLValue,
          hash: cep18TotalSupplyURefKey,
        }
      : null
  );

  const loading = Boolean(
    cep18TotalSupplyURefKey &&
      !cep18TotalSupplyURefRawData &&
      cep18TotalSupplyURefRawLoading
  );

  const calculatedCep18TotalSupply =
    cep18TotalSupplyURefData?.value &&
    cep18TotalSupplyURefData?.type !== 'String'
      ? Big(cep18TotalSupplyURefData?.value)
          .div(tokenDivider(props?.contractPackageInfo?.metadata?.decimals))
          .toString()
      : null;

  const {
    data: { symbol, cep18Balance, decimals },
  } = useGetCep18AmountFromMetadata(props);

  return {
    data: {
      symbol,
      decimals,
      cep18Balance,
      totalSupply: props?.contractPackageInfo?.metadata?.total_supply_uref
        ? calculatedCep18TotalSupply
        : null,
    },
    loading,
  };
};
